import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Box, Skeleton, useTheme } from '@mui/material';
import { formatFloat } from '../../../../../helpers';

export type MaterialBalanceChartPoint = {
    label: string;
    difference_percentage: number | null;
    kilos_consumed: number;
    kilos_produced: number;
    kilos_waste: number;
};

const POSITIVE_COLOR = '#d32f2f';
const NEGATIVE_COLOR = '#1976d2';

function renderTooltip(bg: string, border: string, textColor: string) {
    return function TooltipContent(props: any) {
        const { active, payload, label } = props;
        if (!active || !payload?.length) return null;
        const point: MaterialBalanceChartPoint = payload[0].payload;
        const rows: [string, string][] = [
            ['Bobina consumida', formatFloat(point.kilos_consumed, 0)],
            ['Bolsa producida', formatFloat(point.kilos_produced, 0)],
            ['Merma', formatFloat(point.kilos_waste, 0)],
            [
                'Diferencia',
                point.difference_percentage === null
                    ? 'sin consumo'
                    : `${formatFloat(point.difference_percentage)} %`,
            ],
        ];
        return (
            <div
                style={{
                    backgroundColor: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 12,
                    color: textColor,
                }}
            >
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    {label}
                </div>
                {rows.map(([name, value]) => (
                    <div
                        key={name}
                        style={{ display: 'flex', gap: 8 }}
                    >
                        <span>{name}:</span>
                        <span style={{ fontWeight: 'bold' }}>{value}</span>
                    </div>
                ))}
            </div>
        );
    };
}

/**
 * Difference % per period. The eye should find the bad period before the reader
 * reads a number — that is the whole point of the page.
 *
 * ⚠️ **No tolerance band.** The plan specified a ±3 % band, but that figure was
 * calibrated on the series *before* the packing áreas were excluded from the
 * identity. Post-exclusion the weeks run +2 % … +15 %, so a symmetric ±3 % band
 * would flag almost every week — the same "fires constantly, gets tuned out"
 * failure the plan rejects shift-level alarms for. Drawing a band known to be
 * miscalibrated is worse than drawing none, so only the zero line is shown until
 * open question #1 (the packing-only products) is answered on the floor and the
 * real centre and width are known.
 */
export function MaterialBalanceChart({
    points,
    loading,
}: {
    points: MaterialBalanceChartPoint[];
    loading?: boolean;
}) {
    const theme = useTheme();

    if (loading && points.length === 0) {
        return (
            <Skeleton
                variant={'rectangular'}
                height={280}
                sx={{ borderRadius: 1 }}
            />
        );
    }

    return (
        <Box sx={{ height: 300, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={points}
                    margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{
                            fontSize: 12,
                            fill: theme.palette.text.primary,
                        }}
                    />
                    <YAxis
                        tick={{
                            fontSize: 12,
                            fill: theme.palette.text.primary,
                        }}
                        tickFormatter={(value) => `${formatFloat(value, 0)} %`}
                        width={60}
                    />
                    <ReferenceLine
                        y={0}
                        stroke={theme.palette.text.secondary}
                        strokeWidth={1}
                    />
                    <Tooltip
                        cursor={{ fill: theme.palette.action.hover }}
                        content={renderTooltip(
                            theme.palette.background.paper,
                            theme.palette.divider,
                            theme.palette.text.primary,
                        )}
                    />
                    <Bar
                        dataKey={'difference_percentage'}
                        name={'Diferencia %'}
                        maxBarSize={48}
                    >
                        {/* Sign, not severity: positive means more bobina was
                            eaten than product came out, negative means product
                            came out of bobina never subtracted. Neither is the
                            "good" one, so the two colors read as different, not
                            as better and worse. */}
                        {points.map((point, index) => (
                            <Cell
                                key={index}
                                fill={
                                    (point.difference_percentage ?? 0) >= 0
                                        ? POSITIVE_COLOR
                                        : NEGATIVE_COLOR
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
